import { datadogRum } from '@datadog/browser-rum';
import { getCurrentEnv, HAS_WINDOW } from '@eventbrite/feature-detection';
import { WEB_TRIAL_APP_TOKEN, WEB_TRIAL_CLIENT_TOKEN } from '../constants';
import { RUMOptions } from '../types';

const addEBSessionId = (options: RUMOptions) => {
    if (!HAS_WINDOW) {
        return;
    }
    const sessionId =
        options.ebSessionId || window.__SERVER_DATA__?.request?.session_id;

    //Necessary to wrap the call in onReady as addRumGlobalContext only operates
    //onces RUM instance is already created.
    //https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=cdnasync#add-global-context
    if (sessionId) {
        datadogRum.onReady(() =>
            datadogRum.addRumGlobalContext('session.eb_session_id', sessionId),
        );
    }
};

export const initializeRealUserMonitoring = (options: RUMOptions) => {
    const currentEnv = options.env ? options.env : getCurrentEnv() || undefined;

    if (
        options.enabledEnvironments &&
        currentEnv &&
        !options.enabledEnvironments.includes(currentEnv)
    ) {
        return;
    }

    const clientToken = options.clientToken || WEB_TRIAL_CLIENT_TOKEN;
    const applicationId = options.applicationId || WEB_TRIAL_APP_TOKEN;
    const version = options.version || '';
    const sampleRate = options.sampleRate || 0;
    const service = options.service ? `rum-web--${options.service}` : 'rum-web';

    datadogRum.init({
        ...options,
        applicationId,
        clientToken,
        version,
        service,
        sampleRate,
        site: 'datadoghq.com',
        trackInteractions: true,
        env: currentEnv,
    });

    addEBSessionId(options);
};

/**
 * Thin wrapper to pass a custom action to the RUM tracking instance
 * defined on the window.
 *
 * @generic RUMEvents
 *      The shape of the events that your application supports
 *      eg. type MyEvents = {search: {query: string;}}
 *
 * @param name Top level key defined in the passed in RUMEvents
 *
 * @param data Payload to send for a given event
 */
export const addCustomRUMEvent = <
    Events,
    EventName extends keyof Events = keyof Events
>(
    name: EventName,
    data: Events[EventName],
) => {
    if (
        typeof window !== 'undefined' &&
        window.DD_RUM &&
        window.DD_RUM.addAction
    ) {
        window.DD_RUM.addAction(name, data);
    }
};

export const setUser = datadogRum.setUser;
